import { env } from "@/lib/env";
import { supabase } from "@/data/supabase/client";

/** `lastgo-api` answers every failure as `{ error: { code, message } }`, with
 *  whatever the refusal was measured on alongside them. Callers branch on
 *  `code`, never on `message`. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function isApiError(error: unknown, code?: string): error is ApiError {
  if (!(error instanceof ApiError)) return false;
  return code === undefined || error.code === code;
}

/** A refusal the portal itself decided on, phrased for the partner — thrown
 *  where the API answered something the portal cannot act on. */
export class PortalRefusal extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PortalRefusal";
  }
}

/** Said when a conflict has no sentence of its own: the API explains its
 *  refusals in English and this portal is Czech, so what it wrote never reaches
 *  the partner — the caller adds the specifics it can name. */
export const genericConflictMessage =
  "Změnu jsme neuložili — mezitím se něco změnilo. Načtěte prosím stránku znovu a zkuste to znovu.";

/** The answer the partner is owed. A conflict is a decision somebody made, so
 *  it is always answered; a malformed or server-side failure keeps the caller's
 *  generic error. */
export function refusalMessageOf(error: unknown): string | null {
  if (error instanceof PortalRefusal) return error.message;
  if (isApiError(error) && error.status === 409) return genericConflictMessage;
  return null;
}

/** How many seats the API counted when it refused the edit — the number the
 *  partner is owed, because it is the one the answer was decided on. */
export function soldCountOf(error: unknown): number | null {
  if (!isApiError(error)) return null;
  const sold = error.details.sold;
  return typeof sold === "number" ? sold : null;
}

const baseUrl = env.VITE_API_BASE_URL.replace(/\/$/, "");

async function bearerToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    throw new ApiError(401, "NO_SESSION", "Přihlášení vypršelo. Přihlaste se prosím znovu.");
  }
  return token;
}

async function errorFrom(response: Response): Promise<ApiError> {
  const body: unknown = await response.json().catch(() => null);
  const payload =
    body && typeof body === "object" && "error" in body
      ? (body as { error: unknown }).error
      : null;
  // Older routes answer `{ error: "sentence" }` rather than the coded envelope,
  // and that sentence is the only thing they say about the refusal.
  if (typeof payload === "string") {
    return new ApiError(response.status, "UNKNOWN", payload);
  }
  const envelope =
    payload && typeof payload === "object"
      ? (payload as { code?: string; message?: string } & Record<string, unknown>)
      : null;
  return new ApiError(
    response.status,
    envelope?.code ?? "UNKNOWN",
    envelope?.message ?? "Server neodpověděl podle očekávání.",
    envelope ?? {},
  );
}

/**
 * The one place the portal talks to `lastgo-api`. The partner's own Supabase
 * access token is the credential — the server resolves which businesses it may
 * see, so no business id is ever sent from here.
 */
export async function apiRequest<T>(
  path: string,
  init: { method?: "GET" | "POST" | "PATCH" | "DELETE"; body?: unknown } = {},
): Promise<T> {
  const token = await bearerToken();
  const response = await fetch(`${baseUrl}${path}`, {
    method: init.method ?? "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body === undefined ? {} : { "Content-Type": "application/json" }),
    },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });
  if (!response.ok) throw await errorFrom(response);
  return (await response.json()) as T;
}
